import api from '../api/api';

export interface MetricValue {
  statistic: string;
  value: number;
}

export interface MetricMeasurement {
  statistic: string;
  value: number;
}

export interface MetricResponse {
  name: string;
  description?: string;
  baseUnit?: string;
  measurements: MetricMeasurement[];
  availableTags?: Array<{
    tag: string;
    values: string[];
  }>;
}

export interface HealthResponse {
  status: string;
  components?: Record<string, any>;
}

export interface MetricNames {
  names: string[];
}

export interface EndpointMetric {
  endpoint: string;
  method: string;
  count: number;
  mean: number;
  max: number;
  p50?: number;
  p95?: number;
  p99?: number;
}

export interface SystemMetrics {
  jvmMemoryUsed: number;
  jvmMemoryMax: number;
  jvmMemoryCommitted: number;
  cpuUsage: number;
  systemCpuUsage: number;
  threadCount: number;
  threadPeak: number;
  classesLoaded: number;
  gcPauseCount: number;
  gcPauseTime: number;
}

export interface DatabaseMetrics {
  activeConnections: number;
  idleConnections: number;
  totalConnections: number;
  pendingThreads: number;
  connectionTimeout: number;
  maxConnections: number;
}

class MetricsService {
  private baseUrl = '/actuator';

  /**
   * Get health status
   */
  async getHealth(): Promise<HealthResponse> {
    const response = await api.get<HealthResponse>(`${this.baseUrl}/health`);
    return response.data;
  }

  /**
   * Get all available metric names
   */
  async getMetricNames(): Promise<string[]> {
    const response = await api.get<MetricNames>(`${this.baseUrl}/metrics`);
    return response.data.names;
  }

  /**
   * Get specific metric by name
   */
  async getMetric(name: string, tags?: Record<string, string>): Promise<MetricResponse> {
    let url = `${this.baseUrl}/metrics/${name}`;

    if (tags) {
      const tagParams = Object.entries(tags)
        .map(([key, value]) => `tag=${key}:${value}`)
        .join('&');
      url += `?${tagParams}`;
    }

    const response = await api.get<MetricResponse>(url);
    return response.data;
  }

  /**
   * Get HTTP request metrics grouped by endpoint
   */
  async getEndpointMetrics(): Promise<EndpointMetric[]> {
    try {
      const metric = await this.getMetric('http.server.requests');
      const endpoints: Record<string, EndpointMetric> = {};

      // Group measurements by URI and method
      if (metric.availableTags) {
        const uriTag = metric.availableTags.find(tag => tag.tag === 'uri');
        const methodTag = metric.availableTags.find(tag => tag.tag === 'method');

        if (uriTag && methodTag) {
          for (const uri of uriTag.values) {
            for (const method of methodTag.values) {
              try {
                const endpointMetric = await this.getMetric('http.server.requests', {
                  uri,
                  method
                });

                const key = `${method} ${uri}`;
                const measurements = endpointMetric.measurements;

                endpoints[key] = {
                  endpoint: uri,
                  method,
                  count: measurements.find(m => m.statistic === 'COUNT')?.value || 0,
                  mean: measurements.find(m => m.statistic === 'TOTAL_TIME')?.value || 0,
                  max: measurements.find(m => m.statistic === 'MAX')?.value || 0,
                  p50: measurements.find(m => m.statistic === 'VALUE' && m.statistic.includes('0.5'))?.value,
                  p95: measurements.find(m => m.statistic === 'VALUE' && m.statistic.includes('0.95'))?.value,
                  p99: measurements.find(m => m.statistic === 'VALUE' && m.statistic.includes('0.99'))?.value,
                };

                // Calculate mean from total time and count
                if (endpoints[key].count > 0) {
                  endpoints[key].mean = endpoints[key].mean / endpoints[key].count;
                }
              } catch (error) {
                // Skip endpoints that don't have metrics
                console.debug(`No metrics for ${method} ${uri}`);
              }
            }
          }
        }
      }

      return Object.values(endpoints).sort((a, b) => b.mean - a.mean);
    } catch (error) {
      console.error('Error fetching endpoint metrics:', error);
      return [];
    }
  }

  /**
   * Get JVM and system metrics
   */
  async getSystemMetrics(): Promise<SystemMetrics> {
    try {
      const [
        memUsed,
        memMax,
        memCommitted,
        cpuProcess,
        cpuSystem,
        threads,
        threadsPeak,
        classesLoaded,
        gcCount,
        gcTime
      ] = await Promise.all([
        this.getMetric('jvm.memory.used'),
        this.getMetric('jvm.memory.max'),
        this.getMetric('jvm.memory.committed'),
        this.getMetric('process.cpu.usage'),
        this.getMetric('system.cpu.usage'),
        this.getMetric('jvm.threads.live'),
        this.getMetric('jvm.threads.peak'),
        this.getMetric('jvm.classes.loaded'),
        this.getMetric('jvm.gc.pause').catch(() => null),
        this.getMetric('jvm.gc.pause').catch(() => null),
      ]);

      return {
        jvmMemoryUsed: memUsed.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        jvmMemoryMax: memMax.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        jvmMemoryCommitted: memCommitted.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        cpuUsage: cpuProcess.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        systemCpuUsage: cpuSystem.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        threadCount: threads.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        threadPeak: threadsPeak.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        classesLoaded: classesLoaded.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        gcPauseCount: gcCount?.measurements.find(m => m.statistic === 'COUNT')?.value || 0,
        gcPauseTime: gcTime?.measurements.find(m => m.statistic === 'TOTAL_TIME')?.value || 0,
      };
    } catch (error) {
      console.error('Error fetching system metrics:', error);
      throw error;
    }
  }

  /**
   * Get database connection pool metrics (HikariCP)
   */
  async getDatabaseMetrics(): Promise<DatabaseMetrics | null> {
    try {
      const [active, idle, total, pending, timeout, max] = await Promise.all([
        this.getMetric('hikaricp.connections.active').catch(() => null),
        this.getMetric('hikaricp.connections.idle').catch(() => null),
        this.getMetric('hikaricp.connections').catch(() => null),
        this.getMetric('hikaricp.connections.pending').catch(() => null),
        this.getMetric('hikaricp.connections.timeout').catch(() => null),
        this.getMetric('hikaricp.connections.max').catch(() => null),
      ]);

      if (!active && !idle && !total) {
        return null; // No database metrics available
      }

      return {
        activeConnections: active?.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        idleConnections: idle?.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        totalConnections: total?.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        pendingThreads: pending?.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
        connectionTimeout: timeout?.measurements.find(m => m.statistic === 'COUNT')?.value || 0,
        maxConnections: max?.measurements.find(m => m.statistic === 'VALUE')?.value || 0,
      };
    } catch (error) {
      console.error('Error fetching database metrics:', error);
      return null;
    }
  }

  /**
   * Get error rate (4xx and 5xx responses)
   */
  async getErrorRate(): Promise<{ error4xx: number; error5xx: number; total: number }> {
    try {
      const metric = await this.getMetric('http.server.requests');

      let error4xx = 0;
      let error5xx = 0;
      let total = 0;

      if (metric.availableTags) {
        const statusTag = metric.availableTags.find(tag => tag.tag === 'status');

        if (statusTag) {
          for (const status of statusTag.values) {
            try {
              const statusMetric = await this.getMetric('http.server.requests', { status });
              const count = statusMetric.measurements.find(m => m.statistic === 'COUNT')?.value || 0;

              total += count;

              const statusCode = parseInt(status);
              if (statusCode >= 400 && statusCode < 500) {
                error4xx += count;
              } else if (statusCode >= 500) {
                error5xx += count;
              }
            } catch (error) {
              // Skip
            }
          }
        }
      }

      return { error4xx, error5xx, total };
    } catch (error) {
      console.error('Error fetching error rate:', error);
      return { error4xx: 0, error5xx: 0, total: 0 };
    }
  }

  /**
   * Get requests per second (approximate based on recent metrics)
   */
  async getRequestsPerSecond(): Promise<number> {
    try {
      const metric = await this.getMetric('http.server.requests');
      const count = metric.measurements.find(m => m.statistic === 'COUNT')?.value || 0;

      // This is cumulative, so we'd need to track changes over time
      // For now, return the total count
      return count;
    } catch (error) {
      console.error('Error fetching RPS:', error);
      return 0;
    }
  }
}

export default new MetricsService();
